<template>
  <div
    class="details-container"
    style="overflow: hidden"
    @click.stop="$emit('close')"
  >
    <div class="d-flex flex-column" style="background: #FFFFFF; height: 95vh; width: 780px; margin: auto; overflow: hidden; border-radius: .5em" @click.stop="">
        <header class="flex-shrink-0 py-3">
            <div
                class="col-12 mb-2 pb-2"
                style="border-bottom: #d9d9d9 1px solid"
            >
                <div class="d-flex justify-content-end">
                    <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="showAllPhotos"
                        id="showAllPhotosCheckbox"
                    >
                    <span class="ms-2 me-4">{{ $t('observations.show_all_photos') }}</span>
                    <button class="btn btn-sm btn-secondary mx-1" @click.stop="$emit('close')">{{ $t('common.close') }}</button>
                    <button class="btn btn-sm btn-primary mx-1" :disabled="loading" @click.stop="generatePdf">{{ $t('common.print') }}</button>
                </div>
            </div>
        </header>
        <main ref="mainContent" class="flex-grow-1 overflow-auto p-3" style="overflow-x: hidden !important;">
            <div class="print-page">
                <div class="row">
                    <div class="col-6 nopads">
                        <h1 class="report-header">
                            {{ getTitle() }}
                        </h1>
                        <div v-if="situationDateText">
                            <span class="span-title">{{ $t('road_sign.situation_date')}}</span>
                            <span class="span-text">{{ situationDateText }}</span>
                        </div>
                        <div v-else-if="dateRangeText">
                            <span class="item-title">{{ $t('road_sign.created_range')}}</span>
                            <span class="item-text">{{ dateRangeText }}</span>
                        </div>
                        <div>{{ $t('observations.printed_on') }}: {{ getCurrentDay() }}</div>
                    </div>
                    <div
                        class="col-6 nopads"
                        style="text-align: right"
                    >
                        <img
                            src="../../assets/routa_dark.jpg"
                            class="print-logo"
                            alt="logo"
                        >
                    </div>
                </div>
                <hr>
                <!-- Summary map -->
                <div class="col-12">
                    <div class="map-container summary-map-container">
                        <map-container
                            ref="summaryMapContainer"
                            id="summaryMap"
                            :find-user="false"
                            @onMapChanged="mapChanged"
                        />
                        <road-sign-map
                            v-if="map"
                            ref="signMap"
                            :map="map"
                            :signs="signs"/>
                    </div>
                </div>
                <!-- Summary figures -->
                <vue3-datatable
                    v-if="tableItems.length > 0"
                    ref="datatable"
                    :rows="tableItems"
                    :columns="fields"
                    :pagination="false"
                    :sticky-header="true"
                    :sortable="false"
                    :has-checkbox="false"
                    skin="bh-table-compact"
                    row-class="routa-table-row"
                    cell-class="routa-table-cell"
                >
                    <template #sign="row">
                        <img
                            :src="getImageSource(row.value.sign)"
                            height="25px"
                            alt=""
                            style="margin-right: .5em"
                        /> {{ row.value.name }}
                    </template>
                </vue3-datatable>
            </div>
            <road-sign-print-details
                v-for="sign in signs"
                ref="signs"
                :key="sign.id"
                :sign="sign"
                :report-completed="reportCompleted"
                :show-all-photos="showAllPhotos"
                @remove="removeItem"
            />
        </main>
        <footer class="flex-shrink-0 p-3 border-top">
            <div class="d-flex justify-content-between">
                <button
                    class="btn btn-sm btn-outline-secondary"
                    @click.stop="scrollToTop"
                >
                    ↑
                </button>

                <button
                    class="btn btn-sm btn-outline-secondary"
                    @click.stop="scrollToBottom"
                >
                    ↓
                </button>
            </div>
        </footer>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import MapContainer from "@/components/map/MapContainer";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import {restApi} from "@/components/mixins/RestApiMixin";
import {mapHelper} from '../mixins/MapMixin'
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {imageCacheService} from "@/services/ImageCacheService";
import {pdfMixin} from "@/components/mixins/PdfMixin";
import RoadSignPrintDetails from "./RoadSignPrintDetails.vue";
import RoadSignMap from "./RoadSignMap.vue";
import {roadSignHelper} from "../mixins/RoadSignMixin.js";

export default {
    name: 'RoadSignDetailsReport',
    components: {RoadSignMap, RoadSignPrintDetails, MapContainer, Vue3Datatable},
    mixins: [timeUtils, restApi, mapHelper, pdfMixin, roadSignHelper],
    props: {
        reportSigns: {
            type: Array,
            default() {
                return []
            }
        },
        user: {
            type: Object,
            default: null
        },
        companyPreferences: {
            type: Object,
            default: null
        },
        dateRangeText: {
            type: String,
            default: null
        },
        situationDateText: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            map: null,
            icon: null,
            reportCompleted: false,
            showAllPhotos: false,
            loading: false,
            companyLogo: null,
            signs: [],
            GROUP_ID: 0,
            Z_INDEX: {
                BEHIND: 2,
                INFRONT: 5
            },
            fields: [
                {field: 'sign', title: this.$t('road_sign.sign')},
                {field: 'value', title: this.$t('road_sign.value')},
                {field: 'created', title: this.$t('road_sign.created_at')},
                {field: 'removed', title: this.$t('road_sign.removed_at')}
            ],
            items: [],
            tableItems: [],
        }
    },
    computed: {
    },
    mounted() {
        this.signs = JSON.parse(JSON.stringify(this.reportSigns));
    },
    beforeUnmount() {
        // Allways clear the cache to free memory
        imageCacheService.clear();
    },
    methods: {

        scrollToTop() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        },

        scrollToBottom() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        },

        getTitle() {
            if (this.situationDateText) {
                return this.$t('road_sign.diary')
            }
            return this.$t('road_sign.report_title')
        },

        async generatePdf() {
            this.loading = true

            const {
                pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
            } = await this.initializePdf()

            const screenshot = await this.map.takeScreenShot()

            const summaryMap = await new Response(screenshot).arrayBuffer()

            let title = this.getTitle()
            if (this.situationDateText) {
                title += ' - ' + this.situationDateText
            }

            let nextYPosition = await this.addPdfHeaderWithMap(title, summaryMap, this.observationDateRangeText, this.$t('observations.printed_on'))
            const tablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            const pdfTableItems = await this.preparePdfTableItems(pdfDoc)

            nextYPosition = pdfService.addTable([
                [this.$t('road_sign.sign'), '', this.$t('road_sign.created_at'), this.$t('road_sign.removed_at')],
                ...pdfTableItems], pdfDoc, {
                textFont: fonts.fontText,
                headerFont: fonts.fontBold,
                textFontSize: 9,
                headerFontSize: 9,
                textColor: colors.textColor,
                headerFontColor: colors.boldColor
            }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)

            nextYPosition -= 100

            const afterTablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            if (afterTablePage === tablePage) {
                nextYPosition -= 500
            }
            nextYPosition -= 10

            for (let i = 0; i < this.signs.length; i++) {
                const sign = this.signs[i]
                const photos = await this.fetchPhotos(sign)
                const obj = this.getSign(sign.sign)
                nextYPosition = await this.addSignToPdf(sign, obj?.name, photos, nextYPosition, i)
                // nextYPosition is now updated for the next iteration
            }

            const pdfBytes = await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
            const fileName = "Routa - " + title;
            PdfGeneratorManager.downloadPdf(pdfBytes, this.user && this.user.company && this.user.company.display_name && this.user.company.display_name !== "" ? this.user.company.display_name + " - " + this.$t('common.report') : fileName)
            this.loading = false
        },

        async preparePdfTableItems(pdfDoc) {
            return Promise.all(
                this.items.map(async row => {
                    return Promise.all(
                        row.map(async cellValue => {
                            if (
                                !cellValue ||
                                typeof cellValue !== 'object' ||
                                cellValue.type !== 'image-source'
                            ) {
                                return cellValue
                            }

                            try {
                                const pngBytes = await this.svgUrlToPngBytes(
                                    cellValue.source,
                                    100,
                                    100
                                )

                                const embeddedImage = await pdfDoc.embedPng(pngBytes)

                                return {
                                    type: 'image',
                                    image: embeddedImage,
                                    width: cellValue.width || 25,
                                    height: cellValue.height || 25
                                }
                            } catch (error) {
                                console.error(
                                    'Could not embed PDF table image:',
                                    cellValue.source,
                                    error
                                )

                                return ''
                            }
                        })
                    )
                })
            )
        },

        isPng(photo) {
            return photo.startsWith('data:image/png;');
        },

        async takePhotoOfMap(index) {
            const result = this.$refs.signs[index].getSignsMap()
            const ss = await result.takeScreenShot()
            return await new Response(ss).arrayBuffer();
        },

        async fetchPhotos(sign) {
            const baseUrl = this.signPhotoUrl
            const id = sign.id
            const results = []

            for (const photo of sign.photos) {
                // now we check the cache first
                if (imageCacheService.has(photo.id.toString())) {
                    results.push(imageCacheService.get(photo.id.toString()));
                } else {
                    let url = baseUrl + '/' + id + '/' + photo.id
                    const response = await new Promise(resolve => this.restFetch(url, resolve))
                    results.push(response.data)
                }
            }
            return results
        },

        mapChanged: function (map) {
            this.map = map
            this.initView()
        },

        initView() {
            // Table summaries
            this.initItems()
        },

        initItems() {
            const table = []
            const pdf = []

            this.signs.forEach(item => {
                const sign = this.getSign(item.sign)

                const created = item.created_at
                    ? this.toLocalTime(item.created_at)
                    : ''

                const removedAt = item.removed_at
                    ? this.toLocalTime(item.removed_at)
                    : ''

                /*
                 * PDF data: four columns matching the four PDF headers.
                 * Store the image source now. Embed it later when pdfDoc exists.
                 */
                pdf.push([
                    {
                        type: 'image-source',
                        source: this.getImageSource(item.sign),
                        width: 15,
                        height: 15
                    },
                    sign.name,
                    sign.value,
                    created,
                    removedAt
                ])

                /*
                 * Preview data remains unchanged.
                 */
                table.push({
                    icon: sign.icon,
                    name: sign.name,
                    value: item.value,
                    sign: item.sign,
                    created: created,
                    removed_at: removedAt
                })
            })

            table.sort((a, b) => a.sign.localeCompare(b.sign))

            this.items = pdf
            this.tableItems = table
        },

        removeItem(id) {
            const index = this.signs.findIndex(item => item.id === id)
            if (index >= 0) {
                this.signs.splice(index, 1)
                this.initItems()
            }
        }

    }
}
</script>

<style scoped>
.report-header {
    margin: 0;
    padding-top: .2em;
    font-size: 1.2em;
    font-weight: bold;
    text-transform: uppercase;
}

.summary-map-container {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 1em;
    overflow: hidden;
    width: 750px;
    height: 350px;
    margin: auto;
    margin-bottom: .5em;
}

</style>

